import React, { useCallback } from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { EMPTY_FIELD_DISPLAY } from './forms/viewOnlyFieldUtils';
import {
  isEmptyRichHtml,
  resolveRichHtmlClick,
  richHtmlViewClassName,
  useResolvedRichHtml,
  VIEW_RICH_HTML_CSS,
} from './richHtmlContentShared';
import { openRichContentLink } from '../utils/openRichContentLink';
import {
  inlineImageExpandCss,
  placeClickToExpandCaption,
  withClickToExpandCaptions,
} from './richText/inlineImageExpand';
import { inlineImageAtOrdinal } from '../utils/richContent/inlineImageRef';

import type { StyleProp, ViewStyle } from 'react-native';

export {
  RICH_HTML_TAGS_STYLES,
  htmlSafeForNativeImage,
  htmlForRichHtmlDisplay,
  imageOrdinalForNode,
  omitUndisplayableAttachmentSrcs,
  richHtmlNativeOverflowWidth,
  richHtmlNeedsHorizontalScroll,
  resolveRichHtmlClick,
  toRenderableRichHtml,
  VIEW_RICH_HTML_CSS,
} from './richHtmlContentShared';

export type RichHtmlViewProps = {
  /** ALREADY SANITIZED and display-resolved HTML. This view sanitizes nothing. */
  html: string;
  style?: StyleProp<ViewStyle>;
  testID?: string;
  /**
   * A click on an `<img>` (#1530): its `src` and its ORDINAL among the
   * container's images in document order, because two images can share a
   * src. Absent: images are inert. An image inside a link follows the link.
   */
  onImagePress?: (src: string, ordinal: number) => void;
  /**
   * Which image ordinals `onImagePress` can open. Absent: every picture
   * outside a link shows "Click to expand".
   */
  isImageOpenable?: (ordinal: number) => boolean;
  /**
   * Accepted for parity with the native view's props (#1710) and NOT read:
   * the DOM lays out its own width and nothing here feeds a measurement back.
   * Declared so a shared caller (`MessageBody`) type-checks against either
   * platform file, and never spread onto an element.
   */
  contentWidth?: number;
  /**
   * Request view-mode field. Adds the field-value class so the body uses
   * `colors.textSecondary`. Off keeps the shared view contract, which is
   * what a correspondence message uses.
   */
  viewField?: boolean;
};

/**
 * The render-only core (#1530): the real-DOM view over HTML the caller has
 * already sanitized and resolved. `RichHtmlContent` composes it below with
 * the request-field behaviour unchanged.
 */
export function RichHtmlView({
  html,
  style,
  testID,
  onImagePress,
  isImageOpenable,
  viewField = false,
}: RichHtmlViewProps): React.JSX.Element {
  const onHtmlClick = useCallback(
    (event: {
      target?: EventTarget | null;
      currentTarget?: EventTarget | null;
      preventDefault?: () => void;
      stopPropagation?: () => void;
    }) => {
      const { target, currentTarget } = event;
      if (!(target instanceof Element) || !(currentTarget instanceof Element)) {
        return;
      }
      const click = resolveRichHtmlClick(target, currentTarget, !!onImagePress);
      if (!click) {
        return;
      }
      event.preventDefault?.();
      if (click.kind === 'link') {
        openRichContentLink(click.href);
        return;
      }
      onImagePress?.(click.src, click.ordinal);
    },
    [onImagePress],
  );
  const onHtmlMouseMove = useCallback(
    (event: {
      target?: EventTarget | null;
      clientX?: number;
      clientY?: number;
      nativeEvent?: { clientX?: number; clientY?: number };
    }) => {
      if (!onImagePress) {
        return;
      }
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      const host = target.closest('.ss-image-expand-host');
      const caption = host?.querySelector('.ss-image-expand');
      const clientX = event.clientX ?? event.nativeEvent?.clientX;
      const clientY = event.clientY ?? event.nativeEvent?.clientY;
      if (
        !(host instanceof HTMLElement) ||
        !(caption instanceof HTMLElement) ||
        typeof clientX !== 'number' ||
        typeof clientY !== 'number'
      ) {
        return;
      }
      placeClickToExpandCaption(host, caption, clientX, clientY);
    },
    [onImagePress],
  );

  return (
    <View style={[styles.scrollport, style]} testID={testID}>
      <style>
        {onImagePress
          ? `${VIEW_RICH_HTML_CSS}${inlineImageExpandCss(
              '.ss-rich-html-view',
            )}.ss-rich-html-view .ss-image-expand-host img{cursor:pointer}`
          : VIEW_RICH_HTML_CSS}
      </style>
      {React.createElement('div', {
        className: richHtmlViewClassName(viewField),
        onClick: onHtmlClick,
        onMouseMove: onHtmlMouseMove,
        onMouseOver: onHtmlMouseMove,
        // Sanitized by the caller (toRenderableRichHtml → sanitizeTextArea
        // for a request field, messageHtmlForDisplay for a message).
        dangerouslySetInnerHTML: {
          __html: onImagePress
            ? withClickToExpandCaptions(html, isImageOpenable)
            : html,
        },
      })}
    </View>
  );
}

type RichHtmlContentProps = {
  value: string;
  requestId?: string;
  style?: StyleProp<ViewStyle>;
  emptyText?: string;
  testID?: string;
  onImagePress?: (src: string, ordinal: number) => void;
  /** See `viewField` on `RichHtmlView`. */
  viewField?: boolean;
};

export function RichHtmlContent({
  value,
  requestId,
  style,
  emptyText = EMPTY_FIELD_DISPLAY,
  testID,
  onImagePress,
  viewField = false,
}: RichHtmlContentProps): React.JSX.Element {
  const { safeHtml, resolvedHtml } = useResolvedRichHtml(value, requestId);
  const isImageOpenable = useCallback(
    (ordinal: number) => inlineImageAtOrdinal(safeHtml, ordinal) != null,
    [safeHtml],
  );

  if (!safeHtml || isEmptyRichHtml(safeHtml)) {
    return (
      <Text style={styles.empty} testID={testID}>
        {emptyText}
      </Text>
    );
  }

  return (
    <RichHtmlView
      html={resolvedHtml}
      style={style}
      testID={testID}
      onImagePress={onImagePress}
      isImageOpenable={isImageOpenable}
      viewField={viewField}
    />
  );
}

const styles = StyleSheet.create({
  scrollport: {
    minWidth: 0,
    maxWidth: '100%',
    alignSelf: 'stretch',
    overflowX: 'auto',
  },
  empty: {
    color: '#6B7280',
    fontSize: 14,
    fontStyle: 'italic',
  },
});
